/**
 * 绘制交互：矩形 / 椭圆 / 直线（拖拽创建）、折线（多次点击）、文本（点一下）。
 *
 * 与变换交互同源的做法：**先在树里放一个草稿元素做实时预览**，
 * 松手时把"插入前的父节点快照 + 草稿元素"包成**一条**命令（撤销即整体消失）。
 * 坐标为注入的屏幕→用户映射，因此不依赖真实布局。
 */
import { snapshotOf } from '@/core/patch';
import type { Pt, Rect } from '@/core/geom/matrix';
import type { Command } from '@/core/history';
import {
  buildShape,
  commitShapeCommand,
  isDegenerate,
  pointsAttr,
  type ShapeSpec,
  type ShapeTag,
} from '@/core/ops/create';
import { ui } from '@/stores/editor';
import { isDrawTool, type Tool } from '@/stores/editor';

export interface DrawDeps {
  host: HTMLElement;
  getSvg(): SVGSVGElement | null;
  toRoot(clientX: number, clientY: number): Pt | null;
  tool(): Tool;
  setTool(tool: Tool): void;
  /** 新元素的落点父节点（分组上下文内绘制就落在组里） */
  parent(): Element | null;
  execute(cmd: Command): boolean;
  select(el: Element): void;
  /** 创建完成后的收尾（文本工具在这里立刻进入就地编辑） */
  afterCreate(el: Element, tag: ShapeTag): void;
  syncOverlay(): void;
  /** 新元素的默认样式 */
  style?(): Record<string, string>;
}

interface DragState {
  start: Pt;
  spec: ShapeSpec;
  el: Element;
  before: string;
  parent: Element;
}

/**
 * 指针捕获只是**优化**（保证移出元素后仍收到事件），失败绝不能影响主流程：
 * pointerId 失效（合成事件、指针已被系统收回、Safari 的边界情况）时它会抛 NotFoundError，
 * 若不拦下，后面的"提交命令 + 选中 + 切回工具"就全被跳过 —— 草稿元素会留在文档里。
 */
function capture(host: HTMLElement, id: number | null | undefined): void {
  try {
    if (id !== null && id !== undefined) host.setPointerCapture?.(id);
  } catch {
    /* 指针已失效：忽略 */
  }
}

function release(host: HTMLElement, id: number | null | undefined): void {
  try {
    if (id !== null && id !== undefined) host.releasePointerCapture?.(id);
  } catch {
    /* 同上 */
  }
}

export function attachDrawInteraction(deps: DrawDeps): () => void {
  const { host } = deps;
  let drag: DragState | null = null;
  let points: Pt[] = [];
  let poly: { el: Element; parent: Element; before: string } | null = null;

  const style = (): Record<string, string> => deps.style?.() ?? {};

  const finishPolyline = (commit: boolean): void => {
    if (!poly) return;
    const { el, parent, before } = poly;
    poly = null;
    const kept = dedupe(points); // 双击结束时会补一个重复点
    points = [];

    if (!commit || kept.length < 2) {
      el.remove();
      deps.syncOverlay();
      return;
    }
    el.setAttribute('points', pointsAttr(kept));
    const box = boxOf(kept);
    if (isDegenerate({ tag: 'polyline', box })) {
      el.remove();
      deps.syncOverlay();
      return;
    }
    deps.execute(commitShapeCommand(parent, el, before, '新建 polyline'));
    deps.select(el);
    deps.afterCreate(el, 'polyline');
    deps.syncOverlay();
  };

  const onDown = (e: PointerEvent): void => {
    if (e.button !== 0) return;
    const tool = deps.tool();
    if (!isDrawTool(tool)) return;
    const svg = deps.getSvg();
    const parent = deps.parent();
    if (!svg || !parent) return;
    const at = deps.toRoot(e.clientX, e.clientY);
    if (!at) return;
    e.preventDefault();
    e.stopPropagation(); // 绘制时不做选择/框选

    if (tool === 'text') {
      const spec: ShapeSpec = { tag: 'text', box: { x: at.x, y: at.y, w: 1, h: 0 }, attrs: style() };
      const el = buildShape(svg, spec);
      const before = snapshotOf(parent);
      parent.appendChild(el);
      if (!deps.execute(commitShapeCommand(parent, el, before, '新建 text'))) {
        el.remove();
        return;
      }
      deps.select(el);
      deps.setTool('select');
      deps.afterCreate(el, 'text');
      deps.syncOverlay();
      return;
    }

    if (tool === 'polyline') {
      if (!poly) {
        const el = buildShape(svg, { tag: 'polyline', box: { x: at.x, y: at.y, w: 0, h: 0 }, attrs: style() });
        const before = snapshotOf(parent);
        parent.appendChild(el);
        poly = { el, parent, before };
        points = [at];
        return;
      }
      points.push(at);
      poly.el.setAttribute('points', pointsAttr(points));
      deps.syncOverlay();
      return;
    }

    // rect / ellipse / line：拖拽
    const spec: ShapeSpec = { tag: tool as ShapeTag, box: { x: at.x, y: at.y, w: 0, h: 0 }, attrs: style() };
    const el = buildShape(svg, spec);
    const before = snapshotOf(parent);
    parent.appendChild(el);
    drag = { start: at, spec, el, before, parent };
    capture(host, e.pointerId);
  };

  const onMove = (e: PointerEvent): void => {
    if (!drag) return;
    const at = deps.toRoot(e.clientX, e.clientY);
    if (!at) return;
    const box: Rect = { x: drag.start.x, y: drag.start.y, w: at.x - drag.start.x, h: at.y - drag.start.y };
    drag.spec = { ...drag.spec, box };
    applyPreview(drag.el, drag.spec);
    deps.syncOverlay();
  };

  const onUp = (e: PointerEvent): void => {
    if (!drag) return;
    const { el, spec, parent, before } = drag;
    drag = null;

    if (isDegenerate(spec)) {
      el.remove();
      release(host, e.pointerId);
      deps.syncOverlay();
      return;
    }
    // 先做完"提交 + 选中 + 收尾"，最后才释放捕获（顺序错了就会留下孤儿草稿）
    const committed = deps.execute(commitShapeCommand(parent, el, before, `新建 ${spec.tag}`));
    if (!committed) {
      el.remove();
      release(host, e.pointerId);
      return;
    }
    deps.select(el);
    deps.setTool('select');
    deps.afterCreate(el, spec.tag);
    release(host, e.pointerId);
    deps.syncOverlay();
  };

  /** 指针被系统取消（失焦/触摸被打断）→ 丢弃草稿，不落盘 */
  const onCancel = (): void => {
    if (drag) {
      drag.el.remove();
      drag = null;
      deps.syncOverlay();
      return;
    }
    finishPolyline(true); // 折线已点过的点仍然有效，正常收尾
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && poly) {
      finishPolyline(true);
      return;
    }
    if (e.key === 'Enter' && poly) {
      e.preventDefault();
      finishPolyline(true);
    }
  };

  const onDblClick = (): void => {
    if (poly) finishPolyline(true);
  };

  host.addEventListener('pointerdown', onDown, true);
  host.addEventListener('pointermove', onMove, true);
  host.addEventListener('pointerup', onUp, true);
  host.addEventListener('pointercancel', onCancel, true);
  host.addEventListener('dblclick', onDblClick);
  document.addEventListener('keydown', onKey);

  return () => {
    host.removeEventListener('pointerdown', onDown, true);
    host.removeEventListener('pointermove', onMove, true);
    host.removeEventListener('pointerup', onUp, true);
    host.removeEventListener('pointercancel', onCancel, true);
    host.removeEventListener('dblclick', onDblClick);
    document.removeEventListener('keydown', onKey);
    drag = null;
    points = [];
    poly = null;
  };
}

/** 草稿预览：直接改属性（与拖动预览同一例外） */
function applyPreview(el: Element, spec: ShapeSpec): void {
  const { x, y, w, h } = spec.box;
  const left = Math.min(x, x + w);
  const top = Math.min(y, y + h);
  const width = Math.abs(w);
  const height = Math.abs(h);
  switch (spec.tag) {
    case 'rect':
      el.setAttribute('x', String(left));
      el.setAttribute('y', String(top));
      el.setAttribute('width', String(width));
      el.setAttribute('height', String(height));
      break;
    case 'ellipse':
      el.setAttribute('cx', String(left + width / 2));
      el.setAttribute('cy', String(top + height / 2));
      el.setAttribute('rx', String(width / 2));
      el.setAttribute('ry', String(height / 2));
      break;
    case 'line':
      el.setAttribute('x1', String(x));
      el.setAttribute('y1', String(y));
      el.setAttribute('x2', String(x + w));
      el.setAttribute('y2', String(y + h));
      break;
    default:
      break;
  }
}

/** 去掉末尾连续重合的点（双击结束时会多一个） */
const dedupe = (pts: readonly Pt[]): Pt[] => {
  const out = [...pts];
  while (out.length > 1) {
    const last = out[out.length - 1] as Pt;
    const prev = out[out.length - 2] as Pt;
    if (Math.abs(last.x - prev.x) < 0.01 && Math.abs(last.y - prev.y) < 0.01) out.pop();
    else break;
  }
  return out;
};

const boxOf = (pts: readonly Pt[]): Rect => {
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};

/** 当前是否在绘制某工具（供选择/变换交互让路） */
export const drawActive = (): boolean => isDrawTool(ui.tool);
